import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { 
  Calendar, Clock, BookOpen, BrainCircuit, 
  Trophy, Sparkles, ArrowRight, Sun,
  FileCheck, Library, Compass
} from 'lucide-react';
import StructuredData from '../components/StructuredData';
import '../styles/Academics.scss';

export default function Academics() {
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 150);
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const { t } = useTranslation();

  const navItems = [
    { id: 'holiday-list', label: t('nav_holiday_list') },
    { id: 'academic-calendar', label: t('nav_academic_calendar') },
    { id: 'school-timings', label: t('nav_school_timings') },
    { id: 'school-uniform', label: t('nav_school_uniform') },
    { id: 'class-syllabus', label: t('nav_class_syllabus') },
    { id: 'teaching-methodology', label: t('nav_teaching_methodology') },
    { id: 'examination-schedule', label: t('nav_examination_schedule') },
    { id: 'results', label: t('nav_results') },
    { id: 'co-curricular', label: t('nav_cocurricular') },
  ];

  const holidays = [
    { month: 'January', date: '26 January', day: 'Monday', occasion: 'Republic Day', nature: 'National Holiday' },
    { month: 'February', date: '14 February', day: 'Friday', occasion: 'Saraswati Puja / Vasant Panchami', nature: 'Festival' },
    { month: 'February', date: '26 February', day: 'Wednesday', occasion: 'Maha Shivratri', nature: 'Gazetted' },
    { month: 'March', date: '14 - 15 March', day: 'Friday - Saturday', occasion: 'Holi Festival', nature: 'Festival Break' },
    { month: 'March', date: '31 March', day: 'Monday', occasion: 'Eid-ul-Fitr', nature: 'Gazetted' },
    { month: 'April', date: '06 April', day: 'Sunday', occasion: 'Ram Navami', nature: 'Festival' },
    { month: 'April', date: '14 April', day: 'Monday', occasion: 'Dr. B.R. Ambedkar Jayanti', nature: 'Gazetted' },
    { month: 'May - June', date: '20 May - 18 June', day: 'Tuesday - Wednesday', occasion: 'Summer Vacation', nature: 'Seasonal Break' },
    { month: 'August', date: '15 August', day: 'Friday', occasion: 'Independence Day', nature: 'National Holiday' },
    { month: 'August', date: '27 August', day: 'Wednesday', occasion: 'Janmashtami', nature: 'Festival' },
    { month: 'October', date: '02 October', day: 'Thursday', occasion: 'Mahatma Gandhi Jayanti', nature: 'National Holiday' },
    { month: 'October', date: '19 - 24 October', day: 'Sunday - Friday', occasion: 'Durga Puja Holidays', nature: 'Festival Break' },
    { month: 'November', date: '01 - 07 November', day: 'Saturday - Friday', occasion: 'Diwali & Chhath Puja Holidays', nature: 'Major Festival Break' },
    { month: 'December', date: '25 December', day: 'Thursday', occasion: 'Christmas Day', nature: 'Gazetted' },
    { month: 'Dec - Jan', date: '30 Dec - 08 Jan', day: 'Tuesday - Thursday', occasion: 'Winter Break', nature: 'Seasonal Break' },
  ];

  const academicMonths = [
    { month: 'April', event: 'New Academic Session Begins | Orientation for Playgroup & Class 1 | Baseline Assessments' },
    { month: 'May', event: 'Mother’s Day Celebration | Class Assessments | Summer Vacation Begins (Late May)' },
    { month: 'June', event: 'School Reopens after Summer Break | International Yoga Day Celebration (21 June)' },
    { month: 'July', event: 'Periodic Assessment - 1 (PA-1) | Parent-Teacher Meeting (PTM-1) | Tree Plantation Drive' },
    { month: 'August', event: 'Independence Day Celebrations (15 August) | Science Model Making Exhibition' },
    { month: 'September', event: 'Teachers’ Day (5 Sept) | Half-Yearly / Term-1 Examination | Mid-Term Report Card' },
    { month: 'October', event: 'Gandhi Jayanti | Durga Puja Break | PTM-2 | Inter-House Debate & Elocution' },
    { month: 'November', event: 'Diwali & Chhath Puja Holidays | Children’s Day Celebration (14 Nov) | Coding Sprint' },
    { month: 'December', event: 'Periodic Assessment - 2 (PA-2) | Annual Sports Meet & Athletics Day | Winter Break Starts' },
    { month: 'January', event: 'School Reopens | Republic Day Celebration (26 Jan) | Pre-Board Exams for Class 10' },
    { month: 'February', event: 'Saraswati Puja Celebration | Revision Classes & Remedial Sessions | Admit Card Issue' },
    { month: 'March', event: 'Annual Term-2 Final Examinations | Result Declaration & Award Ceremony | Book Distribution' },
  ];

  const classSyllabus = [
    {
      level: 'Pre-Primary (Play, Nursery, LKG, UKG)',
      desc: 'Foundation stage emphasizing phonetic awareness, basic numeracy, sensory play, motor development, rhyme recitations, and storytelling.',
      subjects: ['English & Phonics', 'Hindi & Balgeet', 'Foundational Maths', 'General Awareness & Environmental Sensitivity', 'Art, Craft & Sensory Play']
    },
    {
      level: 'Primary Wing (Classes 1 to 5)',
      desc: 'Structured cognitive development focusing on linguistic fluency, conceptual mathematics, general science, creative arts, and basic computing.',
      subjects: ['English (Grammar & Reader)', 'Hindi (Vyakaran & Sahitya)', 'Mathematics', 'Environmental Studies (EVS)', 'General Science & Social Studies (Class 3-5)', 'Computer Basics & Coding Logic', 'General Knowledge & Moral Science']
    },
    {
      level: 'Middle Wing (Classes 6 to 8)',
      desc: 'Advanced subject learning promoting analytical reasoning, science experiments, third language introduction, and digital computer lab skills.',
      subjects: ['English Literature & Language', 'Hindi', 'Sanskrit', 'Mathematics', 'Science (Physics, Chemistry, Biology concepts)', 'Social Science (History, Civics, Geography)', 'Computer Science & Scratch Coding', 'Art, Music & Physical Education']
    },
    {
      level: 'Secondary Wing (Classes 9 & 10)',
      desc: 'Comprehensive preparation aligned with the CBSE syllabus framework focusing on board excellence, lab practicals, and career aptitude.',
      subjects: ['English Language & Literature', 'Hindi Course A / Sanskrit', 'Mathematics (Standard / Basic)', 'Science with Lab Practicals (Physics, Chem, Bio)', 'Social Science (History, Geography, Pol. Sci., Economics)', 'Information Technology (IT 402)']
    }
  ];

  const methodologyPillars = [
    { title: 'Interactive Smart Classrooms', desc: 'Audio-visual diagrams, animated science modules, and 3D visual concepts make complex subjects effortless to comprehend.', icon: <Sparkles size={24} /> },
    { title: 'Hands-on Science Practicals', desc: 'Theoretical concepts are tested directly in our composite laboratories through observation, hypothesis, and experimentation.', icon: <BrainCircuit size={24} /> },
    { title: 'Digital Literacy & Coding', desc: 'Students learn computing fundamentals, algorithmic logic, and programming early to prepare for 21st-century technological demands.', icon: <BookOpen size={24} /> },
    { title: 'Continuous Comprehensive Assessment', desc: 'Regular unit tests, viva voce, quizzes, and class projects monitor every child’s individual progression continuously.', icon: <FileCheck size={24} /> },
    { title: 'Remedial & Mentorship Support', desc: 'Dedicated doubt-clearing sessions and personalized remedial attention ensure that no student is left behind.', icon: <Compass size={24} /> },
    { title: 'Values & Discipline in Action', desc: 'Assemblies, moral education, yoga, and community projects instill lifelong integrity, respect, and social responsibility.', icon: <Library size={24} /> },
  ];

  return (
    <div className="academics-page">
      <StructuredData type="organization" />
      <Helmet>
        <title>Academics - Prabhat Tara Vidya Niketan Goraul</title>
        <meta
          name="description"
          content="Academic details of Prabhat Tara Vidya Niketan: Holiday list, Academic calendar, School timings, Syllabus, Uniform schedule, Teaching methodology, Exam schedule, and Results."
        />
        <link rel="canonical" href="https://prabhattaravidyaniketan.in/academics" />
      </Helmet>

      {/* Hero Banner */}
      <div className="hero-banner">
        <span className="badge">
          <BookOpen size={16} /> Academic Excellence & Holistic Growth
        </span>
        <h1>Academics at Prabhat Tara Vidya Niketan</h1>
        <p>
          Empowering curious minds through an enriched curriculum, modern pedagogy, structured examination patterns, and vibrant co-curricular disciplines.
        </p>
      </div>

      {/* Nav Pills Bar */}
      <div className="nav-pills-bar">
        <div className="nav-container">
          {navItems.map((item) => (
            <a key={item.id} href={`#${item.id}`}>
              {item.label}
            </a>
          ))}
        </div>
      </div>

      <div className="main-content">
        
        {/* 1. HOLIDAY LIST */}
        <section id="holiday-list" className="academic-card">
          <span className="card-tag">1. Annual Schedule</span>
          <h2>Official Holiday List (Academic Session)</h2>
          <p className="card-desc">
            The annual list of national holidays, festive observances, and seasonal vacations for Prabhat Tara Vidya Niketan. Any unexpected emergency weather closures will be notified via our official SMS and notices.
          </p>

          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Date</th>
                  <th>Day</th>
                  <th>Occasion / Holiday</th>
                  <th>Type</th>
                </tr>
              </thead>
              <tbody>
                {holidays.map((h, i) => (
                  <tr key={i}>
                    <td><strong>{h.month}</strong></td>
                    <td>{h.date}</td>
                    <td>{h.day}</td>
                    <td><strong>{h.occasion}</strong></td>
                    <td>
                      <span className={`holiday-nature-badge ${
                        h.nature.includes('National') ? 'national' : 
                        h.nature.includes('Break') ? 'break' : 'standard'
                      }`}>
                        {h.nature}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* 2. ACADEMIC CALENDAR */}
        <section id="academic-calendar" className="academic-card">
          <span className="card-tag">2. Session Planner</span>
          <h2>Month-wise Academic Calendar</h2>
          <p className="card-desc">
            A comprehensive roadmap detailing key academic milestones, assessment windows, cultural celebrations, and parent-teacher meetings from April through March.
          </p>

          <div className="academic-months-grid">
            {academicMonths.map((item, idx) => (
              <div key={idx} className="calendar-month-card">
                <div className="month-header">
                  <Calendar size={18} />
                  <strong>{item.month}</strong>
                </div>
                <p>
                  {item.event}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* 3. SCHOOL TIMINGS */}
        <section id="school-timings" className="academic-card">
          <span className="card-tag">3. Daily Timetable</span>
          <h2>School Working Hours & Timings</h2>
          <p className="card-desc">
            Punctuality is a core virtue instilled at Prabhat Tara Vidya Niketan. Students must reach the school campus at least 10 minutes before the morning assembly bell.
          </p>

          <div className="timings-grid">
            <div className="timing-box summer">
              <div className="timing-header">
                <Sun size={22} />
                <h3>Summer Timings</h3>
              </div>
              <ul>
                <li><strong>Assembly & Prayer:</strong> <span>07:00 AM – 07:20 AM</span></li>
                <li><strong>Periods 1 to 4:</strong> <span>07:20 AM – 09:40 AM</span></li>
                <li><strong>Recess / Lunch Break:</strong> <span>09:40 AM – 10:05 AM</span></li>
                <li><strong>Periods 5 to 8:</strong> <span>10:05 AM – 01:00 PM</span></li>
                <li><strong>Student Dispersal:</strong> <span>01:00 PM</span></li>
              </ul>
            </div>

            <div className="timing-box winter">
              <div className="timing-header">
                <Clock size={22} />
                <h3>Winter Timings</h3>
              </div>
              <ul>
                <li><strong>Assembly & Prayer:</strong> <span>08:30 AM – 08:50 AM</span></li>
                <li><strong>Periods 1 to 4:</strong> <span>08:50 AM – 11:10 AM</span></li>
                <li><strong>Recess / Lunch Break:</strong> <span>11:10 AM – 11:35 AM</span></li>
                <li><strong>Periods 5 to 8:</strong> <span>11:35 AM – 02:30 PM</span></li>
                <li><strong>Student Dispersal:</strong> <span>02:30 PM</span></li>
              </ul>
            </div>

            <div className="timing-box office">
              <div className="timing-header">
                <Clock size={22} />
                <h3>Office & Visiting Hours</h3>
              </div>
              <ul>
                <li><strong>Administrative Office:</strong> <span>08:00 AM – 03:30 PM</span></li>
                <li><strong>Fee Counter Hours:</strong> <span>08:30 AM – 02:30 PM</span></li>
                <li><strong>Principal Visiting:</strong> <span>10:00 AM – 12:30 PM</span></li>
                <li><strong>Parent Inquiry Desk:</strong> <span>08:30 AM – 03:00 PM</span></li>
                <li><strong>Sunday & Gazetted:</strong> <span>Closed</span></li>
              </ul>
            </div>
          </div>
        </section>

        {/* 4. SCHOOL UNIFORM */}
        <section id="school-uniform" className="academic-card">
          <span className="card-tag">4. Dress Code & Discipline</span>
          <h2>School Uniform Guidelines</h2>
          <p className="card-desc">
            Wearing the proper school uniform reflects pride, equality, and disciplined character. Students are expected to come in crisp, well-pressed attire with polished black shoes and school ID cards.
          </p>

          <div className="uniform-preview-grid">
            <div>
              <div className="uniform-type-card">
                <h4>Regular School Uniform</h4>
                <p>
                  <strong>Days:</strong> Monday, Tuesday, Thursday, Friday<br />
                  <strong>Attire:</strong> Sky Blue Shirt, Navy Black Trousers / Skirt, School Tie, Belt, Black Oxford Shoes, Navy Socks.
                </p>
              </div>

              <div className="uniform-type-card">
                <h4>House Dress (Sports Day)</h4>
                <p>
                  <strong>Days:</strong> Wednesday & Saturday<br />
                  <strong>Attire:</strong> Respective House Color T-Shirt (Red, Blue, Green, Yellow), White Track Pants / Skirt, White Canvas Shoes & White Socks.
                </p>
              </div>

              <Link to="/uniform-schedule" className="btn-view-uniform">
                View Complete Uniform Timetable & Costs <ArrowRight size={16} />
              </Link>
            </div>

            <div className="uniform-media">
              <img 
                src="/images/dress-code-schedule.jpg" 
                alt="School Dress Code Schedule" 
              />
            </div>
          </div>
        </section>

        {/* 5. CLASS-WISE SYLLABUS */}
        <section id="class-syllabus" className="academic-card">
          <span className="card-tag">5. Curriculum Architecture</span>
          <h2>Class-wise Syllabus Framework</h2>
          <p className="card-desc">
            Aligned with the National Curriculum Framework (NCF) and CBSE guidelines, our syllabus balances foundational literacy, experiential science, mathematics, and expressive arts.
          </p>

          <div className="syllabus-cards-grid">
            {classSyllabus.map((cs, idx) => (
              <div key={idx} className="syllabus-card">
                <div>
                  <h3>{cs.level}</h3>
                  <p className="syllabus-desc">{cs.desc}</p>
                  <strong className="subjects-title">Key Subjects:</strong>
                  <ul>
                    {cs.subjects.map((s, si) => (
                      <li key={si}>{s}</li>
                    ))}
                  </ul>
                </div>
                <div className="card-footer">
                  <a href="/admission-enquiry">
                    Request Class Syllabus Book <ArrowRight size={14} />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* 6. TEACHING METHODOLOGY */}
        <section id="teaching-methodology" className="academic-card">
          <span className="card-tag">6. Pedagogical Innovation</span>
          <h2>Modern Teaching Methodology</h2>
          <p className="card-desc">
            Education at PTVN moves away from rote memorization towards inquiry-driven exploration, conceptual mastery, and experiential discovery.
          </p>

          <div className="grid-cards">
            {methodologyPillars.map((m, idx) => (
              <div key={idx} className="info-card">
                <div className="icon-wrap">{m.icon}</div>
                <h3>{m.title}</h3>
                <p>{m.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* 7. EXAMINATION SCHEDULE */}
        <section id="examination-schedule" className="academic-card">
          <span className="card-tag">7. Assessment Architecture</span>
          <h2>Examination Schedule & Evaluation Scheme</h2>
          <p className="card-desc">
            Our assessment framework ensures structured, stress-free evaluation of cognitive progress, practical competencies, and scholastic aptitude.
          </p>

          <div className="table-responsive">
            <table>
              <thead>
                <tr>
                  <th>Examination Tier</th>
                  <th>Window / Month</th>
                  <th>Weightage</th>
                  <th>Syllabus Coverage</th>
                  <th>Key Focus</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>Periodic Assessment 1 (PA-1)</strong></td>
                  <td>Last Week of July</td>
                  <td>10%</td>
                  <td>April - July Units</td>
                  <td>Unit concepts & foundational recall</td>
                </tr>
                <tr>
                  <td><strong>Half-Yearly / Term-1 Exam</strong></td>
                  <td>Mid September</td>
                  <td>40%</td>
                  <td>Cumulative Term-1 Syllabus</td>
                  <td>Comprehensive written + Practical test</td>
                </tr>
                <tr>
                  <td><strong>Periodic Assessment 2 (PA-2)</strong></td>
                  <td>First Week of December</td>
                  <td>10%</td>
                  <td>October - November Units</td>
                  <td>Application-based problem solving</td>
                </tr>
                <tr>
                  <td><strong>Pre-Board Examination (Class 10)</strong></td>
                  <td>Mid January</td>
                  <td>Internal Benchmark</td>
                  <td>100% Board Syllabus</td>
                  <td>CBSE Board simulation & answer formatting</td>
                </tr>
                <tr>
                  <td><strong>Annual Final Examination (Term-2)</strong></td>
                  <td>First Two Weeks of March</td>
                  <td>40%</td>
                  <td>Full Academic Year (Prescribed)</td>
                  <td>Final promotion, mark sheet & rank awards</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="exam-corner-link-banner">
            <span>Looking for recent exam time tables, sample papers, or question banks?</span>
            <Link to="/exam-corner">
              Open Exam Corner &rarr;
            </Link>
          </div>
        </section>

        {/* 8. RESULTS & ACADEMIC PERFORMANCE */}
        <section id="results" className="academic-card">
          <span className="card-tag">8. Hall of Fame</span>
          <h2>Results & Academic Performance</h2>
          <p className="card-desc">
            With a proud 30-year legacy of academic distinction, Prabhat Tara Vidya Niketan consistently delivers 100% pass results in secondary board examinations.
          </p>

          <div className="results-preview-grid">
            <div className="results-copy">
              <div className="stats-highlight-grid">
                <div className="stat-card-green">
                  <h3>100%</h3>
                  <p>Board Pass Rate</p>
                </div>
                <div className="stat-card-gold">
                  <h3>95%+</h3>
                  <p>Highest Marks Scored</p>
                </div>
              </div>
              <p>
                Our students regularly secure top percentile positions in CBSE/State board examinations, qualifying for leading engineering, medical, civil service, and commercial streams. Every topper is felicitated with special merit scholarships and trophies.
              </p>
            </div>

            <div className="results-media">
              <img 
                src="/descriptive-image/school-toppers-class-x-congratulations-2.png" 
                alt="Prabhat Tara Vidya Niketan Class X Toppers" 
              />
            </div>
          </div>
        </section>

        {/* 9. CO-CURRICULAR ACTIVITIES */}
        <section id="co-curricular" className="academic-card">
          <span className="card-tag">9. Beyond Textbooks</span>
          <h2>Co-curricular & Creative Activities</h2>
          <p className="card-desc">
            True excellence encompasses sports, stage presence, digital creativity, and cultural arts. Every student participates in structured co-curricular clubs.
          </p>

          <div className="grid-cards">
            <div className="info-card">
              <div className="icon-wrap" style={{ color: '#27ae60' }}><Trophy size={24} /></div>
              <h3>Sports & Physical Athletics</h3>
              <p>Cricket, Football, Badminton, Kho-Kho, Volleyball, Track Races, and Yoga drills develop teamwork, resilience, and stamina.</p>
            </div>
            <div className="info-card">
              <div className="icon-wrap" style={{ color: '#ff6b2b' }}><Sparkles size={24} /></div>
              <h3>Classical & Cultural Dance</h3>
              <p>Professional dance and music instructors guide students in folk traditions, classical performances, and annual function choreography.</p>
            </div>
            <div className="info-card">
              <div className="icon-wrap" style={{ color: '#0c278c' }}><BrainCircuit size={24} /></div>
              <h3>Computer Coding & STEM Club</h3>
              <p>Hands-on logic puzzles, scratch visual programming, web design basics, and science project prototypes ignite future innovation.</p>
            </div>
            <div className="info-card">
              <div className="icon-wrap" style={{ color: '#9b27af' }}><Library size={24} /></div>
              <h3>Debates, Quiz & Public Speaking</h3>
              <p>Inter-house debate contests, recitation competitions, and morning assembly anchoring build stage confidence and communication fluency.</p>
            </div>
          </div>

          <div className="co-curricular-cta">
            <Link to="/gallery">
              Explore Our Activities Photo & Video Gallery <ArrowRight size={18} />
            </Link>
          </div>
        </section>

      </div>
    </div>
  );
}

